import { useCallback, useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronRight, CircleDollarSign, History, ExternalLink, FileBarChart, LogOut, Menu, Moon, ReceiptText, RefreshCw, ShieldCheck, Store, Sun, Wallet, X } from 'lucide-react';
import { io } from 'socket.io-client';
import { API_URL, api, session } from './api.js';
import { can, isStaff } from './ui.jsx';
import { DailyBookPage, ShiftHistoryPage, ShiftsPage } from './pages/Shifts.jsx';
import { BillsPage, RevenuePage } from './pages/Bills.jsx';
import { PayrollPage } from './pages/Payroll.jsx';

const managerUrl = import.meta.env.VITE_MANAGER_URL || 'http://localhost:5173';
// salary.oneplayplatform.com: trang Tính lương riêng (như booking.oneplayplatform.com của Managers) — không có thanh menu.
const HOSTNAME = typeof window !== 'undefined' ? window.location.hostname : '';
const SALARY_HOST = HOSTNAME.startsWith('salary.');
const SALARY_URL = !SALARY_HOST && HOSTNAME.endsWith('oneplayplatform.cloud') ? 'https://salary.oneplayplatform.com' : null;
let handoffStarted = false;

/** Mở trang Tính lương riêng ở tab mới và chuyển phiên đăng nhập sang (không phải đăng nhập lại). */
async function openSalaryTab(event) {
  event.preventDefault();
  const tab = window.open('', '_blank');
  try {
    const { code } = await api.post('/auth/handoff', {});
    if (tab) { tab.opener = null; tab.location.href = `${SALARY_URL}/#handoff=${encodeURIComponent(code)}`; }
    else window.location.href = `${SALARY_URL}/#handoff=${encodeURIComponent(code)}`;
  } catch {
    if (tab) tab.location.href = SALARY_URL;
  }
}
const reportPermissions = ['reports.branch', 'reports.franchise', 'reports.region', 'reports.system'];

function Logo({ compact = false }) {
  return <div className={`brand ${compact ? 'compact' : ''}`}><img src="/assets/logo-oneplay.png" alt="OnePlay" /><div><strong>OnePlay</strong><span>BILLING</span></div></div>;
}

function Notice({ notice, onClose }) {
  // Thông báo thành công tự ẩn; lỗi giữ lại đến khi người dùng đóng.
  useEffect(() => {
    if (notice?.type !== 'success') return undefined;
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [notice]);
  if (!notice) return null;
  return <div className={`notice ${notice.type || 'info'}`}><span>{notice.message}</span><button onClick={onClose}><X size={16} /></button></div>;
}

function ThemeToggle({ theme, setTheme, compact = false }) {
  const isLight = theme === 'light';
  return <button
    type="button"
    className={`theme-toggle ${compact ? 'compact' : ''}`}
    onClick={() => setTheme(isLight ? 'dark' : 'light')}
    title={isLight ? 'Chuyển sang giao diện tối' : 'Chuyển sang giao diện sáng'}
    aria-label={isLight ? 'Chuyển sang giao diện tối' : 'Chuyển sang giao diện sáng'}
  >
    {isLight ? <Moon size={16} /> : <Sun size={16} />}
    <span>{isLight ? 'Tối' : 'Sáng'}</span>
  </button>;
}

function LoginPage({ onLogin, notice, setNotice, theme, setTheme, salary = false }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const submit = async (event) => {
    event.preventDefault(); setLoading(true); setNotice(null);
    try { await api.login(form.email, form.password); await onLogin(); } catch (error) { setNotice({ type: 'error', message: error.message }); } finally { setLoading(false); }
  };
  return <main className="login-shell">
    <div className="login-theme"><ThemeToggle theme={theme} setTheme={setTheme} /></div>
    <div className="login-aurora one" /><div className="login-aurora two" />
    <section className="login-story">
      <Logo />
      <div className="hero-chip"><ShieldCheck size={15} /> Hệ thống tính tiền One Play</div>
      <h1>Tính tiền.<br /><span>Chốt ca.</span></h1>
      <p>Mở phiên chơi theo máy, tính tiền giờ theo bảng giá, lập hóa đơn và đối soát quỹ tiền mặt theo ca.</p>
      <div className="hero-metrics"><div><strong>Một tài khoản</strong><span>Dùng chung với One Play Managers</span></div><div><strong>Đồng bộ</strong><span>Cơ sở, máy, booking và bảng giá lấy từ Managers</span></div></div>
    </section>
    <section className="login-card">
      <div className="mobile-logo"><Logo /></div>
      <p className="eyebrow">{salary ? 'ONEPLAY · TÍNH LƯƠNG' : 'ONEPLAY BILLING'}</p><h2>{salary ? 'Đăng nhập Tính lương' : 'Đăng nhập'}</h2><p>{salary ? 'Dành cho Chủ nhượng quyền và Quản lý cơ sở. Dùng tài khoản One Play Managers.' : 'Dùng tài khoản One Play Managers của bạn.'}</p>
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <form onSubmit={submit}>
        <label>Email<input autoComplete="username" type="email" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
        <label>Mật khẩu<input autoComplete="current-password" type="password" required value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} /></label>
        <button className="btn primary wide" disabled={loading}>{loading ? <RefreshCw className="spin" size={18} /> : <ShieldCheck size={18} />} Đăng nhập</button>
      </form>
      <small>Mọi thao tác thu tiền và chốt ca đều được ghi nhật ký.</small>
    </section>
  </main>;
}

const navItems = [
  ['/app/shifts', CircleDollarSign, 'Ca tiền mặt', ['cash_shifts.view']],
  ['/app/daily', CalendarDays, 'Sổ ca theo ngày', ['cash_shifts.view'], true],
  ['/app/history', History, 'Lịch sử ca', ['cash_shifts.view'], true],
  ['/app/bills', ReceiptText, 'Hóa đơn', ['cash_shifts.view']],
  ['/app/revenue', FileBarChart, 'Doanh thu', reportPermissions, true],
  ['/app/payroll', Wallet, 'Tính lương', ['payroll.prepare', 'payroll.configure'], true],
];

function AppLayout({ auth, onLogout, notice, setNotice, theme, setTheme }) {
  const [mobileNav, setMobileNav] = useState(false);
  const [live, setLive] = useState(false);
  const location = useLocation();
  const stores = auth.stores || [];

  const [storeId, setStoreIdState] = useState(() => {
    const saved = sessionStorage.getItem('oneplay_billing_store');
    return stores.some((s) => String(s.id) === String(saved)) ? String(saved) : String(stores[0]?.id || '');
  });

  const setStoreId = useCallback((newId) => {
    sessionStorage.setItem('oneplay_billing_store', String(newId));
    setStoreIdState(String(newId));
    window.dispatchEvent(new CustomEvent('oneplay:store_changed', { detail: { storeId: String(newId) } }));
  }, []);

  useEffect(() => {
    if (storeId) sessionStorage.setItem('oneplay_billing_store', String(storeId));
  }, [storeId]);

  // mục thứ 5 = true: trang tổng hợp nhiều ngày, không dành cho nhân viên
  const items = navItems.filter(([, , , permissions, managerOnly]) => !(managerOnly && isStaff(auth)) && permissions.some((permission) => can(auth, permission)));
  const blocked = isStaff(auth) && navItems.some(([path, , , , managerOnly]) => managerOnly && location.pathname === path);
  const current = items.find(([path]) => location.pathname === path);
  useEffect(() => setMobileNav(false), [location.pathname]);
  useEffect(() => {
    const notify = (eventName, payload) => window.dispatchEvent(new CustomEvent('oneplay:realtime', { detail: { eventName, payload } }));
    // Token được đọc lại ở mỗi lần kết nối để dùng access token mới sau khi làm mới phiên.
    const socket = io(API_URL.replace(/\/api\/v1$/, ''), { auth: (callback) => callback({ token: session.accessToken }) });
    let missedEvents = false;
    socket.onAny(notify);
    socket.on('connect', () => { setLive(true); if (missedEvents) notify('sync', null); missedEvents = false; });
    socket.on('disconnect', () => { setLive(false); missedEvents = true; });
    socket.on('connect_error', async (error) => {
      setLive(false); missedEvents = true;
      if (error.message === 'Unauthorized' && await api.refresh()) socket.connect();
    });
    return () => socket.disconnect();
  }, []);
  return <div className="app-shell">
    <aside className={mobileNav ? 'sidebar open' : 'sidebar'}>
      <div className="sidebar-top"><Logo compact /><button className="icon-btn close-nav" onClick={() => setMobileNav(false)}><X /></button></div>
      <nav>
        <div className="nav-group"><p>Tính tiền</p>{items.map(([path, Icon, label]) => SALARY_URL && path === '/app/payroll' ? <a key={path} href={SALARY_URL} target="_blank" rel="noreferrer" onClick={openSalaryTab}><Icon size={18} /><span>{label}</span><ExternalLink className="nav-arrow" size={15} /></a> : <NavLink key={path} to={path} className={({ isActive }) => (isActive ? 'active' : '')}><Icon size={18} /><span>{label}</span><ChevronRight className="nav-arrow" size={15} /></NavLink>)}</div>
        <div className="nav-group"><p>Liên kết</p><a href={managerUrl} target="_blank" rel="noreferrer"><ExternalLink size={18} /><span>One Play Managers</span></a></div>
      </nav>
      <div className="sidebar-user"><div className="avatar">{auth.user.full_name?.slice(0, 1)}</div><div><strong>{auth.user.full_name}</strong><span>{auth.user.role_key}</span></div><button className="icon-btn" onClick={onLogout} title="Đăng xuất"><LogOut size={17} /></button></div>
    </aside>
    <section className="workspace">
      <header className="topbar">
        <div className="topbar-title"><button className="icon-btn menu-btn" onClick={() => setMobileNav(true)}><Menu /></button><div><small>ONEPLAY BILLING</small><strong>{current?.[2] || 'Tính tiền'}</strong></div></div>
        <div className="topbar-actions">
          {stores.length > 1 ? (
            <div className="topbar-store-switcher" title="Chọn cơ sở làm việc">
              <Store size={15} />
              <select
                value={storeId}
                onChange={(event) => setStoreId(event.target.value)}
                aria-label="Chọn cơ sở"
              >
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          ) : stores[0] ? (
            <div className="topbar-single-store">
              <Store size={15} />
              <span>{stores[0].name}</span>
            </div>
          ) : null}
          <ThemeToggle theme={theme} setTheme={setTheme} compact />
          <span className={live ? 'live-dot' : 'live-dot offline'}>{live ? 'TRỰC TUYẾN' : 'MẤT KẾT NỐI · ĐANG THỬ LẠI'}</span>
        </div>
      </header>
      <Notice notice={notice} onClose={() => setNotice(null)} />
      {blocked ? <Navigate to="/app/shifts" replace /> : items.length ? <Outlet context={{ auth, setNotice, storeId, setStoreId, stores }} /> : <main className="content-page"><div className="card ops-empty">Tài khoản của bạn chưa được cấp quyền dùng hệ thống tính tiền. Liên hệ quản lý để được cấp quyền ca tiền mặt trong One Play Managers.</div></main>}
    </section>
  </div>;
}

/** salary.oneplayplatform.com: chỉ trang Tính lương, thanh trên tối giản. */
function SalaryLayout({ auth, onLogout, notice, setNotice, theme, setTheme }) {
  const canView = ['payroll.prepare', 'payroll.configure'].some((permission) => can(auth, permission));
  return <div className="salary-shell">
    <header className="topbar">
      <div className="topbar-title"><Logo compact /><div><small>ONEPLAY</small><strong>Tính lương</strong></div></div>
      <div className="topbar-actions">
        <ThemeToggle theme={theme} setTheme={setTheme} compact />
        <span className="salary-user">{auth.user.full_name}</span>
        <button className="icon-btn" onClick={onLogout} title="Đăng xuất"><LogOut size={17} /></button>
      </div>
    </header>
    <Notice notice={notice} onClose={() => setNotice(null)} />
    {canView ? <Outlet context={{ auth, setNotice, stores: auth.stores || [] }} /> : <main className="content-page"><div className="card ops-empty">Trang Tính lương dành cho Chủ nhượng quyền và Quản lý cơ sở. Nhân viên xem lương của mình trong OnePlay Station → Lương của tôi.</div></main>}
  </div>;
}

export default function App() {
  const [auth, setAuth] = useState(null);
  const handoffCode = SALARY_HOST ? new URLSearchParams(window.location.hash.slice(1)).get('handoff') : null;
  const [loading, setLoading] = useState(Boolean(session.accessToken || handoffCode));
  const [notice, setNotice] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('oneplay_billing_theme') === 'light' ? 'light' : 'dark');
  const navigate = useNavigate();
  const bootstrap = useCallback(async () => { setLoading(true); try { setAuth(await api.get('/auth/me')); } catch { session.clear(); setAuth(null); } finally { setLoading(false); } }, []);
  useEffect(() => {
    if (!handoffCode) { if (session.accessToken) bootstrap(); return; }
    if (handoffStarted) return; // mã dùng một lần
    handoffStarted = true;
    window.history.replaceState(null, '', window.location.pathname);
    api.redeemHandoff(handoffCode)
      .then(() => bootstrap())
      .catch((error) => { setNotice({ type: 'error', message: error.message }); if (session.accessToken) bootstrap(); else setLoading(false); });
  }, [bootstrap]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'light' ? '#f4f7fb' : '#090b17');
    localStorage.setItem('oneplay_billing_theme', theme);
  }, [theme]);
  const logout = async () => { await api.logout(); setAuth(null); setNotice(null); navigate('/login'); };
  if (loading) return <div className="splash"><Logo /><div className="loader"><RefreshCw className="spin" /> Đang đồng bộ với One Play Managers...</div></div>;
  if (SALARY_HOST) return <Routes>
    <Route path="/login" element={auth ? <Navigate to="/" replace /> : <LoginPage salary onLogin={bootstrap} notice={notice} setNotice={setNotice} theme={theme} setTheme={setTheme} />} />
    <Route path="/" element={auth ? <SalaryLayout auth={auth} onLogout={logout} notice={notice} setNotice={setNotice} theme={theme} setTheme={setTheme} /> : <Navigate to="/login" replace />}>
      <Route index element={<PayrollPage />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>;
  return <Routes>
    <Route path="/login" element={auth ? <Navigate to="/app/shifts" replace /> : <LoginPage onLogin={bootstrap} notice={notice} setNotice={setNotice} theme={theme} setTheme={setTheme} />} />
    <Route path="/app" element={auth ? <AppLayout auth={auth} onLogout={logout} notice={notice} setNotice={setNotice} theme={theme} setTheme={setTheme} /> : <Navigate to="/login" replace />}>
      <Route index element={<Navigate to="shifts" replace />} />
      <Route path="shifts" element={<ShiftsPage />} />
      <Route path="daily" element={<DailyBookPage />} />
      <Route path="history" element={<ShiftHistoryPage />} />
      <Route path="bills" element={<BillsPage />} />
      <Route path="revenue" element={<RevenuePage />} />
      <Route path="payroll" element={<PayrollPage />} />
    </Route>
    <Route path="*" element={<Navigate to={auth ? '/app/shifts' : '/login'} replace />} />
  </Routes>;
}
